/**
 * Inverted index for fast full-text search
 * Maps tokens to documents with field tracking for boosting
 */

import { tokenize } from './tokenizer';
import type { Webpage } from '@/types';

// Fields to index with their boost weights
export const FIELD_BOOST: Record<IndexableField, number> = {
  title: 10,
  notes: 5,
  description: 3,
  site_name: 2,
  author: 2,
  url: 1,
  content: 1,
};

export type IndexableField = 'title' | 'notes' | 'description' | 'site_name' | 'author' | 'url' | 'content';

// A posting entry links a token to a document field
export interface PostingEntry {
  docId: string;
  field: IndexableField;
  frequency: number; // How many times token appears in this field
}

// Document metadata for BM25 scoring
export interface DocMetadata {
  id: string;
  tokenCount: number; // Total tokens across all fields
  fieldTokenCounts: Partial<Record<IndexableField, number>>;
}

// The main inverted index structure
export interface InvertedIndex {
  // token -> list of posting entries
  postings: Map<string, PostingEntry[]>;
  // docId -> document metadata
  docs: Map<string, DocMetadata>;
  // Corpus statistics
  totalDocs: number;
  avgDocLength: number;
  // Version for cache invalidation
  version: number;
}

/**
 * Create an empty inverted index
 */
export function createIndex(): InvertedIndex {
  return {
    postings: new Map(),
    docs: new Map(),
    totalDocs: 0,
    avgDocLength: 0,
    version: 1,
  };
}

/**
 * Build an inverted index from a list of webpages
 */
export function buildIndex(webpages: Webpage[]): InvertedIndex {
  const index = createIndex();

  for (const webpage of webpages) {
    addDocument(index, webpage);
  }

  // Calculate average document length
  if (index.totalDocs > 0) {
    let totalTokens = 0;
    for (const doc of index.docs.values()) {
      totalTokens += doc.tokenCount;
    }
    index.avgDocLength = totalTokens / index.totalDocs;
  }

  return index;
}

/**
 * Add a single document to the index
 */
export function addDocument(index: InvertedIndex, webpage: Webpage): void {
  const docId = webpage.id;

  // Remove existing document if present (for updates)
  if (index.docs.has(docId)) {
    removeDocument(index, docId);
  }

  const fieldTokenCounts: Partial<Record<IndexableField, number>> = {};
  let totalTokenCount = 0;

  // Index each field
  const fieldsToIndex: Array<{ field: IndexableField; text: string | null }> = [
    { field: 'title', text: webpage.title },
    { field: 'description', text: webpage.description },
    { field: 'url', text: webpage.url },
    { field: 'site_name', text: webpage.site_name },
    { field: 'author', text: webpage.author },
    { field: 'content', text: webpage.content },
    { field: 'notes', text: webpage.notes },
  ];

  for (const { field, text } of fieldsToIndex) {
    if (!text) continue;

    const tokens = tokenize(text);
    if (tokens.length === 0) continue;

    fieldTokenCounts[field] = tokens.length;
    totalTokenCount += tokens.length;

    // Count token frequencies in this field
    const tokenFreq = new Map<string, number>();
    for (const token of tokens) {
      tokenFreq.set(token, (tokenFreq.get(token) || 0) + 1);
    }

    // Add to postings
    for (const [token, frequency] of tokenFreq) {
      const posting: PostingEntry = { docId, field, frequency };

      if (!index.postings.has(token)) {
        index.postings.set(token, []);
      }
      index.postings.get(token)!.push(posting);
    }
  }

  // Store document metadata
  index.docs.set(docId, {
    id: docId,
    tokenCount: totalTokenCount,
    fieldTokenCounts,
  });

  index.totalDocs++;

  // Update average document length
  let totalTokens = 0;
  for (const doc of index.docs.values()) {
    totalTokens += doc.tokenCount;
  }
  index.avgDocLength = index.totalDocs > 0 ? totalTokens / index.totalDocs : 0;
}

/**
 * Remove a document from the index
 */
export function removeDocument(index: InvertedIndex, docId: string): void {
  if (!index.docs.has(docId)) return;

  // Remove from all postings
  for (const [token, postings] of index.postings) {
    const filtered = postings.filter(p => p.docId !== docId);
    if (filtered.length === 0) {
      index.postings.delete(token);
    } else {
      index.postings.set(token, filtered);
    }
  }

  // Remove document metadata
  index.docs.delete(docId);
  index.totalDocs = Math.max(0, index.totalDocs - 1);

  // Update average document length
  if (index.totalDocs > 0) {
    let totalTokens = 0;
    for (const doc of index.docs.values()) {
      totalTokens += doc.tokenCount;
    }
    index.avgDocLength = totalTokens / index.totalDocs;
  } else {
    index.avgDocLength = 0;
  }
}

/**
 * Get all documents containing a token
 */
export function getPostings(index: InvertedIndex, token: string): PostingEntry[] {
  return index.postings.get(token.toLowerCase()) || [];
}

/**
 * Get documents matching a prefix (for autocomplete)
 */
export function getPostingsByPrefix(index: InvertedIndex, prefix: string): Map<string, PostingEntry[]> {
  const result = new Map<string, PostingEntry[]>();
  const lowerPrefix = prefix.toLowerCase();

  for (const [token, postings] of index.postings) {
    if (token.startsWith(lowerPrefix)) {
      result.set(token, postings);
    }
  }

  return result;
}

/**
 * Get the number of documents containing a token (for IDF calculation)
 */
export function getDocumentFrequency(index: InvertedIndex, token: string): number {
  const postings = index.postings.get(token.toLowerCase());
  if (!postings) return 0;

  // Count unique documents
  const docIds = new Set(postings.map(p => p.docId));
  return docIds.size;
}

/**
 * Serialize index for storage (converts Maps to arrays)
 */
export function serializeIndex(index: InvertedIndex): string {
  const serializable = {
    postings: Array.from(index.postings.entries()),
    docs: Array.from(index.docs.entries()),
    totalDocs: index.totalDocs,
    avgDocLength: index.avgDocLength,
    version: index.version,
  };
  return JSON.stringify(serializable);
}

/**
 * Deserialize index from storage
 */
export function deserializeIndex(data: string): InvertedIndex | null {
  try {
    const parsed = JSON.parse(data);
    return {
      postings: new Map(parsed.postings),
      docs: new Map(parsed.docs),
      totalDocs: parsed.totalDocs,
      avgDocLength: parsed.avgDocLength,
      version: parsed.version,
    };
  } catch (e) {
    console.warn('Failed to deserialize search index:', e);
    return null;
  }
}

